import {
  type BubbleChartDataPointModel,
  type BubbleChartViewModelSourceData,
  type FieldData,
  isBubbleChartFieldInfo,
  getAxisTitlesForReferenceChart,
  getAxisTitlesForReferenceChartWithDirection,
  safeViewNodeDimensions,
  sortDataPointsByCluster,
} from '@ardoq/bubble-chart';
import { type EnhancedScopeData } from '@ardoq/data-model';
import {
  componentUtils,
  enhancedScopeDataOperations,
  filterColorDataOperations,
  resolveFilterColor,
  type FilterColorData,
  type FilterColorEntry,
} from '@ardoq/scope-data';
import {
  APIEntityType,
  type ArdoqId,
  type FilterInterfaceFilter,
} from '@ardoq/api-types';
import { vizFilterGray } from '@ardoq/design-tokens';
import { type I18nLocale } from '@ardoq/i18n';
import {
  formatOtherLabel,
  getFirstFormattedMultiLabel,
  getLabelWithFormattingForViewsNotSupportingMultilabeling,
  getOtherLabelsDataForReference,
  type LabelsInterface,
} from '@ardoq/graph';
import { isTruthy } from '@ardoq/common-helpers';
import { isNil } from 'lodash';
import {
  type ReferenceDataToViewNodeBaseArgs,
  type ReferenceWithBubbleComponent,
  type ResetReferenceBubbleChartBaseArgs,
} from './types';
import {
  getReferenceFieldPresenceDataWithScopeData,
  selectedFieldValueWithScopeData,
} from '../util';

type ReferenceDataToViewNodeWithScopeDataArgs =
  ReferenceDataToViewNodeBaseArgs & {
    enhancedScopeData: EnhancedScopeData;
    filterColorData: FilterColorData;
    i18nLocale: I18nLocale;
    referenceLabelFilters: FilterInterfaceFilter[];
    labelsInterface: LabelsInterface;
    reference: ReferenceWithBubbleComponent;
  };

const getBubbleFilterColor = (
  filterColorData: FilterColorData,
  referenceId: ArdoqId,
  bubbleComponentId: ArdoqId
): FilterColorEntry | undefined => {
  const referenceFilterColor =
    filterColorDataOperations.getReferenceFilterColor(
      filterColorData,
      referenceId
    );
  if (referenceFilterColor && referenceFilterColor.color !== vizFilterGray) {
    return referenceFilterColor;
  }
  return (
    filterColorDataOperations.getComponentFilterColor(
      filterColorData,
      bubbleComponentId
    ) ?? referenceFilterColor
  );
};

const toReferenceDataPointModelWithScopeData = ({
  enhancedScopeData,
  filterColorData,
  i18nLocale,
  reference,
  selectedFieldNameX,
  selectedFieldNameY,
  selectedFieldNameRadius,
  referenceLabelFilters,
  labelsInterface,
}: ReferenceDataToViewNodeWithScopeDataArgs): BubbleChartDataPointModel | null => {
  const { referenceId, bubbleComponentId } = reference;

  const [selectedXValue, selectedYValue] = [
    selectedFieldNameX,
    selectedFieldNameY,
  ].map(field =>
    selectedFieldValueWithScopeData(enhancedScopeData, reference, field)
  );
  const selectedRadiusValue =
    selectedFieldNameRadius && typeof selectedFieldNameRadius !== 'string'
      ? selectedFieldValueWithScopeData(
          enhancedScopeData,
          reference,
          selectedFieldNameRadius
        )
      : 1;

  if (isNil(selectedXValue) || isNil(selectedYValue)) {
    return null;
  }

  const filterColor = getBubbleFilterColor(
    filterColorData,
    referenceId,
    bubbleComponentId
  );

  const { fill, stroke } = filterColor
    ? resolveFilterColor(filterColor.color, filterColor.version, {
        useAsBackgroundStyle: false,
      })
    : componentUtils.getComponentColorAsSVGAttributes(
        enhancedScopeData,
        bubbleComponentId,
        { useAsBackgroundStyle: false }
      );

  const componentLabel =
    getLabelWithFormattingForViewsNotSupportingMultilabeling({
      modelId: bubbleComponentId,
      entityLabel:
        enhancedScopeDataOperations.getComponent(
          enhancedScopeData,
          bubbleComponentId
        )?.name ?? '',
      entityType: APIEntityType.COMPONENT,
      i18nLocale,
      referenceLabelFilters,
      labelsInterface,
    });

  const referenceLabelsData = getOtherLabelsDataForReference(
    labelsInterface,
    referenceId
  );
  const referenceLabel =
    referenceLabelsData &&
    getFirstFormattedMultiLabel(
      labelsInterface,
      referenceId,
      referenceLabelsData,
      APIEntityType.REFERENCE,
      i18nLocale
    );

  return {
    cid: bubbleComponentId,
    uniqueId: referenceId,
    ...safeViewNodeDimensions(
      selectedXValue,
      selectedYValue,
      selectedRadiusValue
    ),
    class:
      componentUtils.getComponentCSSClassNames({
        scopeData: enhancedScopeData,
        componentId: bubbleComponentId,
      }) ?? '',
    fill,
    stroke,
    label: referenceLabel
      ? `${componentLabel} [${formatOtherLabel(referenceLabel)}]`
      : componentLabel,
    isContext: false,
  };
};

type ResetWithScopeDataArgs = Omit<
  ResetReferenceBubbleChartBaseArgs,
  'incomingReferences' | 'outgoingReferences'
> & {
  availableBubbleComponentFields: FieldData[];
  availableReferenceFields: FieldData[];
  availableReferencedComponentFields: FieldData[];
  enhancedScopeData: EnhancedScopeData;
  filterColorData: FilterColorData;
  i18nLocale: I18nLocale;
  referenceLabelFilters: FilterInterfaceFilter[];
  labelsInterface: LabelsInterface;
  incomingReferences: ReferenceWithBubbleComponent[];
  outgoingReferences: ReferenceWithBubbleComponent[];
};

export const resetReferenceBubbleChartWithScopeData = (
  previousState: BubbleChartViewModelSourceData,
  {
    viewSettings,
    incomingReferences,
    outgoingReferences,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    numericFields,
    viewpointModeAttributes,
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    hasReferenceBubbleChartFeature = false,
    enhancedScopeData,
    filterColorData,
    i18nLocale,
    referenceLabelFilters,
    labelsInterface,
  }: ResetWithScopeDataArgs
): BubbleChartViewModelSourceData => {
  const {
    selectedReferenceTypeName,
    selectedReferenceDirection,
    selectedFieldNameX,
    selectedFieldNameY,
    selectedFieldNameRadius,
    showBubblesWithZeroValue: viewSettingsShowBubblesWithZeroValue,
  } = viewSettings;

  const showBubblesWithZeroValue =
    viewSettingsShowBubblesWithZeroValue === undefined ||
    viewSettingsShowBubblesWithZeroValue;

  const references =
    selectedReferenceDirection === 'incoming'
      ? incomingReferences
      : outgoingReferences;

  const isViewpointMode = viewpointModeAttributes !== undefined;

  const shouldUseNewAxisLabels =
    isViewpointMode && hasReferenceBubbleChartFeature;

  const { xAxisTitle, yAxisTitle } = shouldUseNewAxisLabels
    ? getAxisTitlesForReferenceChart(viewSettings, {
        availableBubbleComponentFields,
        availableReferenceFields,
        availableReferencedComponentFields,
      })
    : getAxisTitlesForReferenceChartWithDirection(viewSettings);

  const usedReferences = numericFields.length
    ? references.filter(
        ({ referenceTypeName }) =>
          referenceTypeName === selectedReferenceTypeName
      )
    : [];

  const unfilteredData: BubbleChartDataPointModel[] =
    !isBubbleChartFieldInfo(selectedFieldNameX) ||
    !isBubbleChartFieldInfo(selectedFieldNameY)
      ? []
      : usedReferences
          .map(reference =>
            toReferenceDataPointModelWithScopeData({
              enhancedScopeData,
              filterColorData,
              i18nLocale,
              reference,
              selectedFieldNameX,
              selectedFieldNameY,
              selectedFieldNameRadius,
              referenceLabelFilters,
              labelsInterface,
            })
          )
          .filter(isTruthy);

  const filteredData = unfilteredData.filter(
    bubble => showBubblesWithZeroValue || bubble.radius
  );

  const data = sortDataPointsByCluster(filteredData);

  const isFilteredByRadius = Boolean(
    !viewSettings.showBubblesWithZeroValue &&
    !filteredData.length &&
    unfilteredData.length
  );

  const emptyFields = getReferenceFieldPresenceDataWithScopeData(
    enhancedScopeData,
    usedReferences,
    selectedFieldNameX,
    selectedFieldNameY,
    selectedFieldNameRadius
  );

  return {
    ...previousState,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    availableBubbleComponentFields,
    availableReferenceFields,
    availableReferencedComponentFields,
    numericFields,
    data,
    xAxisTitle,
    yAxisTitle,
    viewSettings,
    emptyFields,
    isFilteredByRadius,
    viewpointModeAttributes,
  };
};
